import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Save, FileDown, Target } from 'lucide-react';
import authedFetch from '../../lib/authedFetch';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StepKpisOfInterest({ orgId, state, onSave, saving, shareToken }) {
  const [draft, setDraft] = useState(state.answers || {});
  const [kpis, setKpis] = useState([]);
  useEffect(() => { setDraft(state.answers || {}); }, [state]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        // Use the org's resolved profile so we show the right KPIs + baselines for THIS industry.
        const url = `${API}/api/organizations/${orgId}/roi-profile`;
        const r = shareToken ? await fetch(url) : await authedFetch(url);
        if (r.ok) {
          const d = await r.json();
          setKpis(d.resolved?.kpis || []);
        }
      } catch { /* noop */ }
    })();
  }, [orgId, shareToken]);

  const togglePriority = (kpiId) => {
    setDraft((d) => {
      const cur = d.kpi_priorities || [];
      const next = cur.includes(kpiId) ? cur.filter((x) => x !== kpiId) : [...cur, kpiId];
      return { ...d, kpi_priorities: next };
    });
  };

  const setBaseline = (kpiId, val) => {
    setDraft((d) => ({
      ...d,
      kpi_baselines: [
        ...(d.kpi_baselines || []).filter((b) => b.id !== kpiId),
        ...(val !== '' && val != null ? [{ id: kpiId, baseline_per_employee: Number(val) }] : []),
      ],
    }));
  };

  const baselineFor = (kpiId) => {
    const row = (draft.kpi_baselines || []).find((b) => b.id === kpiId);
    return row?.baseline_per_employee ?? '';
  };

  const downloadTemplate = async () => {
    const url = `${API}/api/onboarding/template/kpis_of_interest?org_id=${orgId}`;
    const r = shareToken ? await fetch(url) : await authedFetch(url);
    const csv = await r.text();
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'seertech_kpi_baselines_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-5" data-testid="onboarding-step-kpis">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          Industry defaults pre-fill. Tick the KPIs your CFO will ask about. Override the per-employee $/yr baseline only when you have actuals.
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="onb-kpis-template-btn">
          <FileDown className="h-3.5 w-3.5 mr-2" /> CSV template
        </Button>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-700">Must-defend KPI <span className="text-rose-500">*</span></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="onb-must-defend-grid">
          {kpis.map((k) => {
            const active = draft.must_defend_kpi === k.id;
            return (
              <button
                type="button"
                key={k.id}
                onClick={() => set('must_defend_kpi', k.id)}
                className={`text-left rounded-md border p-2 ${active ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid={`onb-must-defend-${k.id}`}
              >
                <div className="flex items-center gap-1.5">
                  <Target className={`h-3 w-3 ${active ? 'text-rose-500' : 'text-slate-400'}`} />
                  <span className={`text-xs font-semibold ${active ? 'text-rose-900' : 'text-slate-800'}`}>{k.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-700">Priority KPIs (tick all that apply) <span className="text-rose-500">*</span></div>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-2 text-left">Priority</th>
                <th className="px-2 py-2 text-left">KPI</th>
                <th className="px-2 py-2 text-right">Industry default ($/employee/yr)</th>
                <th className="px-2 py-2 text-right">Your override ($)</th>
              </tr>
            </thead>
            <tbody>
              {kpis.map((k) => (
                <tr key={k.id} className="border-t border-slate-100" data-testid={`onb-kpi-row-${k.id}`}>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={(draft.kpi_priorities || []).includes(k.id)}
                      onChange={() => togglePriority(k.id)}
                      data-testid={`onb-kpi-priority-${k.id}`}
                    />
                  </td>
                  <td className="px-2 py-2 font-medium">{k.name}</td>
                  <td className="px-2 py-2 text-right font-mono text-slate-500">
                    {k.baseline_per_employee?.toLocaleString?.() ?? '—'}
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      placeholder={String(k.baseline_per_employee ?? '')}
                      value={baselineFor(k.id)}
                      onChange={(e) => setBaseline(k.id, e.target.value)}
                      className="h-7 text-xs"
                      data-testid={`onb-kpi-baseline-${k.id}`}
                    />
                  </td>
                </tr>
              ))}
              {kpis.length === 0 && (
                <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">Pick an industry first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(draft)} disabled={saving} data-testid="onb-kpis-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
          Save & continue
        </Button>
      </div>
    </div>
  );
}
